/**
 * Calculates the total ISA (Individual Savings Account) contribution for a given person,
 * combining both regular investments and ad-hoc contributions, and normalizes the result
 * to either annual or monthly units as specified.
 *
 * @param {Array<Object>} investments - Array of investment objects, each containing at least:
 *   - {string} type - The type of investment (should be "ISA" to be included).
 *   - {string} name - The name of the person associated with the investment.
 *   - {Object} contribution - The contribution details:
 *     - {string|number} value - The contribution amount.
 *     - {string} unit - The unit of the contribution ("Monthly" or "Annual").
 * @param {Array<Object>} adHocs - Array of ad-hoc contribution objects, each containing at least:
 *   - {string} type - The type of ad-hoc (should be "ISA" to be included).
 *   - {string} adHocType - The ad-hoc type (should be "Contribution" to be included).
 *   - {string} name - The name of the person associated with the ad-hoc.
 *   - {Object} amount - The ad-hoc amount details:
 *     - {string|number} value - The contribution amount.
 *     - {string} unit - The unit of the contribution ("Monthly" or "Annual").
 * @param {string} [unit="Annual"] - The unit to normalize the total contribution to ("Annual" or "Monthly").
 * @param {string} [personName=""] - The name of the person whose contributions are being totaled.
 * @returns {number} The total ISA contribution for the specified person, normalized to the specified unit.
 */
const getTotalIsaContribution = (
  investments = [],
  adHocs = [],
  unit = "Annual",
  personName = ""
) => {
  const totalFromInvestments = investments
    .filter((inv) => inv.type === "ISA" && inv.name === personName)
    .reduce((sum, inv) => {
      const val = parseFloat(inv?.contribution?.value || 0);
      const isMonthly = inv?.contribution?.unit === "Monthly";
      return (
        sum +
        (unit === "Monthly" && !isMonthly
          ? val / 12
          : unit === "Annual" && isMonthly
          ? val * 12
          : val)
      );
    }, 0);

//   console.log(adHocs);
  const totalFromAdHocs = adHocs
    .filter(
      (a) =>
        a.type === "ISA" &&
        a.adHocType === "Contribution" &&
        a.name === personName
    )
    .reduce((sum, a) => {
      const val = parseFloat(a?.amount?.value || 0);
      const isMonthly = a?.amount?.unit === "Monthly";
      return (
        sum +
        (unit === "Monthly" && !isMonthly
          ? val / 12
          : unit === "Annual" && isMonthly
          ? val * 12
          : val)
      );
    }, 0);

//   console.log("Total ISA Contributions", {
//     totalFromInvestments,
//     totalFromAdHocs,
//     unit,
//   });
  return totalFromInvestments + totalFromAdHocs;
};

export default getTotalIsaContribution;
